import * as React from "react";
import { useEffect, useState } from "react";
import PatientSummary from "../patient-profile/PatientSummary";
import { fillTemplateString, unescapePathOf } from "../commons/utils/text-rendering";
import sfetch from "../commons/utils/super-fetch";
import * as Sentry from "@sentry/browser";
import { ProfileCompletion } from "./ProfileCompletion";
import AssessmentResultPreview, {
  AssessmentResultPreviewProps,
} from "../assessment-result/AssessmentResultPreview";
import LoadingSpinner from "../flow/utils/LoadingSpinner";
import { PatientProfileWithConfiguration } from "../patient-profile/PatientProfile";
import { HashRouter, Route, Routes } from "react-router-dom";
import NavigablePatientBar from "../patient-profile/NavigablePatientBar";
import Country from "../commons/model/address/Country";
import PatientCard from "../patient-profile/PatientCard";
import { getPatientLabelledData } from "../patient-profile/PatientData";
import { ErrorCode } from "../commons/utils/errorCode";
import Error from "../commons/components/Error";

interface PatientDashboardView {
  patientProfileWithConfiguration: PatientProfileWithConfiguration;
  latestAssessmentResult?: AssessmentResultPreviewProps;
  virtualConsultationUrl: string;
}

export function PatientProfilePanel({
  patients,
  fetchPatientUrl,
  availableCountries,
}: {
  patients: PatientSummary[];
  fetchPatientUrl: string;
  availableCountries: Country[];
}) {
  return (
    <>
      <div id="profile-panel" className="panel with-shadow">
        {patients.length > 1 ? (
          <HashRouter>
            <NavigablePatientBar patients={patients} />
            <Routes>
              {patients.map((patientSummary: PatientSummary, index: number) => (
                <Route
                  path={index == 0 ? "/" : `/${patientSummary.uid}`}
                  key={patientSummary.uid}
                  element={
                    <LoadingPatientTab
                      patientUid={patientSummary.uid}
                      forMyself={index == 0}
                      availableCountries={availableCountries}
                      fetchPatientUrl={fetchPatientUrl}
                    />
                  }
                />
              ))}
            </Routes>
          </HashRouter>
        ) : (
          <LoadingPatientTab
            patientUid={patients[0].uid}
            forMyself={true}
            availableCountries={availableCountries}
            fetchPatientUrl={fetchPatientUrl}
          />
        )}
      </div>
    </>
  );
}

function LoadingPatientTab({
  patientUid,
  forMyself,
  availableCountries,
  fetchPatientUrl,
}: {
  patientUid: string;
  forMyself: boolean;
  availableCountries: Country[];
  fetchPatientUrl: string;
}) {
  const [patient, setPatient] = useState<PatientDashboardView>(null);
  const [errorMessageCode, setErrorMessageCode] = useState<string>();

  const setCurrentPatientDashboardView = (patientDashboardView: PatientDashboardView) => {
    setPatient(patientDashboardView);
  };

  useEffect(() => {
    const url = fillTemplateString(unescapePathOf(fetchPatientUrl), {
      uid: patientUid,
    });
    sfetch(url, {
      method: "GET",
    })
      .then((it) => it.json())
      .then(setCurrentPatientDashboardView)
      .catch((error) => {
        error.json().then((response) => {
          if (response.code === ErrorCode.PATIENT_OR_DEPENDENT_NOT_LINKED_TO_DOCTOR) {
            setErrorMessageCode("LegalGuardian.MissingDependent.Error");
          } else {
            Sentry.captureException(error);
            setErrorMessageCode("Global.TechnicalError");
          }
        });
      });
  }, []);

  if (errorMessageCode) {
    return <Error errorCode={errorMessageCode} />;
  }

  return patient ? (
    <PatientTab patient={patient} forMyself={forMyself} availableCountries={availableCountries} />
  ) : (
    <div className={"spinner-container"}>
      <LoadingSpinner />
    </div>
  );
}

function PatientTab({
  patient,
  forMyself,
  availableCountries,
}: {
  patient: PatientDashboardView;
  forMyself: boolean;
  availableCountries: Country[];
}) {
  return (
    <>
      <div id="profile-info">
        <div>
          <div className="react-panel-title">
            {i18n["Page.Portal.PatientDashboard.ProfileInformation.Title"]}
          </div>
          <PatientCard
            patient={patient.patientProfileWithConfiguration.patientProfile.personalInformation}
            patientData={getPatientLabelledData}
            isPatientView={true}
            legalGuardians={[]}
            showLeadStatus={patient.patientProfileWithConfiguration.showLeadStatus}
          />
        </div>
        {patient.patientProfileWithConfiguration.isLinkedToADoctor && (
          <ProfileCompletion
            patientProfileWithConfiguration={patient.patientProfileWithConfiguration}
            availableCountries={availableCountries}
            forMyself={forMyself}
          />
        )}
      </div>
      {patient.patientProfileWithConfiguration.isLinkedToADoctor && (
        <LastAssessmentResultPanel currentPatient={patient} />
      )}
    </>
  );
}

function LastAssessmentResultPanel({ currentPatient }) {
  return currentPatient.latestAssessmentResult ? (
    <AssessmentResultPreview isPatientView={true} {...currentPatient.latestAssessmentResult} />
  ) : (
    <StartVirtualConsultationAction
      virtualConsultationUrl={currentPatient.virtualConsultationUrl}
    />
  );
}

function StartVirtualConsultationAction({ virtualConsultationUrl }) {
  return (
    <div className="start-virtual-consultation h-flex space-between">
      <span>{i18n["Page.Portal.PatientDashboard.LatestReport.NoReport"]}</span>
      <a className="button-secondary small" href={virtualConsultationUrl}>
        {i18n["Page.Portal.PatientDashboard.StartVirtualConsultation.Button"]}
      </a>
    </div>
  );
}
